import { Train, Play } from 'lucide-react';
import type { Product } from './types';

export const passengerTransportationManagement: Product = {
  id: 'passenger-transportation-management',
  title: 'Passenger Transportation Management',
  subtitle: 'AI-Powered Fleet Operations, Scheduling & Passenger Experience Platform',
  description: 'Comprehensive transportation management platform combining AI-powered scheduling, real-time fleet tracking, passenger experience optimization, and compliance management for bus operators, rail services, ferry companies, and multi-modal transit authorities.',
  icon: Train,
  image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Transportation & Logistics',
  status: 'Live',
  businessTypes: ['#transportation', '#transit', '#mobility', '#fleet-management', '#passenger-services'],
  features: [
    // Fleet & Vehicle Management (8 features)
    'AI-Powered Fleet Scheduling & Route Optimization',
    'Real-Time Vehicle Tracking with GPS & Telematics',
    'Predictive Maintenance & Vehicle Health Monitoring',
    'Driver Management & Shift Scheduling System',
    'Vehicle Allocation & Resource Planning Tools',
    'Fuel Management & Consumption Analytics',
    'Fleet Performance Dashboards & KPI Tracking',
    'Multi-Modal Fleet Support (Bus, Rail, Ferry, Coach)',
    
    // Passenger Experience (8 features)
    'Real-Time Passenger Information Systems (RTPI)',
    'Mobile Ticketing & E-Payment Integration',
    'Journey Planning & Multi-Modal Trip Booking',
    'Accessibility Features & Special Assistance Management',
    'Customer Feedback & Satisfaction Tracking',
    'Loyalty Programs & Passenger Rewards System',
    'Real-Time Delay Notifications & Disruption Alerts',
    'On-Board Entertainment & WiFi Management',
    
    // Operations & Scheduling (7 features)
    'Intelligent Timetable Generation & Optimization',
    'Crew Rostering & Fatigue Management',
    'Depot Management & Vehicle Allocation',
    'Service Disruption Management & Recovery Planning',
    'Inter-Modal Connection Coordination',
    'Demand-Responsive Transport Scheduling',
    'Special Event & Charter Service Management',
    
    // Revenue & Ticketing (7 features)
    'Multi-Channel Ticket Sales Platform',
    'Dynamic Pricing & Yield Management',
    'Fare Collection & Revenue Assurance',
    'Concession & Partner Fare Management',
    'Season Ticket & Subscription Management',
    'Contactless Payment & Mobile Wallet Support',
    'Revenue Analytics & Financial Reporting',
    
    // Analytics & Intelligence (8 features)
    'AI Demand Forecasting & Capacity Planning',
    'Passenger Flow Analytics & Crowding Detection',
    'Route Performance & Profitability Analysis',
    'Carbon Footprint Tracking & Sustainability Reports',
    'Service Quality Benchmarking & KPIs',
    'Predictive Analytics for Operational Planning',
    'Real-Time Business Intelligence Dashboards',
    'Competitive Analysis & Market Intelligence',
    
    // Compliance & Safety (6 features)
    'Regulatory Compliance Management (DVSA, ORR, DfT)',
    'Driver License & Certification Tracking',
    'Safety Incident Reporting & Investigation',
    'CCTV Integration & Security Monitoring',
    'Audit Trail & Complete Activity Logging',
    'GDPR & Data Protection Compliance',
    
    // Enterprise Security (6 features)
    'End-to-End Data Encryption & Secure APIs',
    'Single Sign-On (SSO) & Multi-Factor Authentication',
    'Role-Based Access Control & User Management',
    'Disaster Recovery & Business Continuity',
    'SOC 2 & ISO 27001 Certified Infrastructure',
    '24/7 Security Monitoring & Threat Detection'
  ],
  pricing: { setup: 45000, monthly: 2400 },
  color: 'from-emerald-600 to-teal-700',
  demoConfig: {
    type: 'interactive',
    url: '/passenger-transportation/demo',
    title: 'Experience the Complete Platform',
    description: 'Explore our AI-powered transportation management system with fleet operations, passenger services, and revenue analytics',
    buttonText: 'Launch Transport Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
  }
};
